import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import {
  Video, Award, Globe2, Star,
  ClipboardCheck, Bot as BotIcon, Gauge, CloudCog,
  Smartphone, Brain, ShieldCheck, Workflow,
  Building, Laptop, Layers,
  Quote
} from "lucide-react";

const IMPACT_STATS = [
  { icon: Video, value: "500+", label: "Instructor-Led Workshops", desc: "Hands-on testing workshops delivered by experienced QA and automation experts." },
  { icon: Award, value: "100+", label: "Enterprise Trainers", desc: "Industry-certified instructors with expertise across manual, automation, and AI-powered testing." },
  { icon: Globe2, value: "25+", label: "Global Technology Partners", desc: "Learning aligned with leading software vendors and testing ecosystems." },
  { icon: Star, value: "98%", label: "Client Satisfaction", desc: "Organizations continue to choose Skillsgain for high-quality technical learning experiences." }
];

const LEARNING_OUTCOMES = [
  { icon: ClipboardCheck, title: "Manual Testing", desc: "Learn software testing fundamentals, test planning, test case design, defect lifecycle management, exploratory testing, and quality validation techniques for enterprise applications." },
  { icon: BotIcon, title: "Test Automation", desc: "Develop expertise in automation frameworks using Selenium, Cypress, Playwright, API testing, and regression automation to improve testing efficiency and release quality." },
  { icon: Gauge, title: "Performance Testing", desc: "Evaluate application speed, scalability, stability, and responsiveness using industry-standard performance testing methodologies and enterprise performance engineering practices." },
  { icon: CloudCog, title: "Cloud Testing", desc: "Validate cloud-based applications across different environments by testing scalability, availability, security, compatibility, and cloud infrastructure performance." },
  { icon: Smartphone, title: "Mobile Application Testing", desc: "Test Android and iOS applications through functional, usability, compatibility, security, and device-specific testing to deliver consistent mobile experiences." },
  { icon: Brain, title: "AI-Powered Testing", desc: "Explore intelligent test generation, self-healing automation, predictive defect analysis, and AI-assisted quality engineering for modern software delivery." },
  { icon: ShieldCheck, title: "Software Quality Assurance (QA)", desc: "Strengthen software quality through structured QA processes, risk-based testing, compliance validation, defect prevention, and continuous quality improvement." },
  { icon: Workflow, title: "Continuous Testing & DevTestOps", desc: "Integrate testing throughout CI/CD pipelines with automated validation, continuous feedback, release readiness assessments, and DevOps testing practices." }
];

const DELIVERY_MODELS = [
  { icon: Video, title: "Live Virtual Training", desc: "Interactive instructor-led sessions with practical testing exercises, automation demonstrations, and enterprise case studies." },
  { icon: Building, title: "Classroom Training", desc: "Hands-on workshops focused on real-world software testing scenarios and collaborative learning." },
  { icon: Laptop, title: "Self-Paced Learning", desc: "Access recorded sessions, labs, assessments, and practice exercises at your preferred pace." },
  { icon: Layers, title: "Blended Learning", desc: "A combination of live instruction, digital learning, practical assignments, and enterprise testing projects." }
];

const BY_THE_NUMBERS = [
  { value: "250+", label: "Enterprise Clients" },
  { value: "200+", label: "Corporate Learning Programs" },
  { value: "25+", label: "Global Technology Partners" },
  { value: "98%", label: "Client Satisfaction" }
];

const TestingPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      <PageHero
        eyebrow="ENTERPRISE TESTING & QUALITY ASSURANCE LEARNING"
        title="Software Testing & Quality Assurance Training for Enterprise Teams"
        subtitle="Improve software quality with Skillsgain's enterprise Software Testing Training programs covering Manual Testing, Automation Testing, AI Testing, Quality Assurance (QA), performance testing, security testing, and modern testing frameworks. Instructor-led learning combines practical labs, enterprise projects, and industry best practices."
      />

      {/* Enterprise Learning Impact */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">ENTERPRISE LEARNING IMPACT</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Trusted learning experiences for software quality and testing excellence</h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {IMPACT_STATS.map((o, i) => (
            <Reveal key={o.label} variant="up" delay={i * 80}>
              <div className="sg-card bg-white border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center mb-4">
                  <o.icon size={20} className="text-[#6BBE32]"/>
                </div>
                <div className="text-3xl font-bold text-[#15348C]">{o.value}</div>
                <div className="text-sm font-semibold text-[#15348C] mt-1">{o.label}</div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">{o.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Software Testing Capabilities */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">SOFTWARE TESTING CAPABILITIES</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Practical testing expertise for modern software development</h2>
            <p className="mt-3 text-gray-600">Gain hands-on experience with manual testing, automation frameworks, AI-powered testing, and quality assurance practices through instructor-led learning and enterprise projects.</p>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {LEARNING_OUTCOMES.map((m, i) => (
            <Reveal key={m.title} variant="up" delay={(i % 4) * 80}>
              <div className="sg-card group bg-[#F8F9FA] border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#15348C] flex items-center justify-center mb-4 group-hover:bg-[#6BBE32] transition-colors">
                  <m.icon size={20} className="text-white"/>
                </div>
                <h4 className="font-bold text-[#15348C]">{m.title}</h4>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{m.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Learning Experiences */}
      <section className="bg-[#F8F9FA] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">LEARNING EXPERIENCES</div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Learning options designed for enterprise QA teams</h2>
              <p className="mt-3 text-gray-600">Choose instructor-led virtual learning, classroom workshops, self-paced modules, or blended learning programs based on your testing requirements and business priorities.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DELIVERY_MODELS.map((m, i) => (
              <Reveal key={m.title} variant="up" delay={i * 80}>
                <div className="flex flex-col gap-3 bg-white border border-gray-100 rounded-xl p-5 h-full sg-card">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center">
                    <m.icon size={18} className="text-[#6BBE32]"/>
                  </div>
                  <div className="font-bold text-[#15348C] text-sm">{m.title}</div>
                  <p className="text-xs text-gray-500 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* By the Numbers + Customer Success Story */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          <Reveal variant="up" className="lg:col-span-1">
            <div className="bg-[#15348C] text-white rounded-2xl p-8 h-full relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="relative">
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">BY THE NUMBERS</div>
                <div className="space-y-5">
                  {BY_THE_NUMBERS.map(s => (
                    <div key={s.label}>
                      <div className="text-3xl font-bold">{s.value}</div>
                      <div className="text-xs text-gray-300">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal variant="up" delay={100} className="lg:col-span-2">
            <div className="bg-[#F8F9FA] border border-gray-100 rounded-2xl p-8 h-full flex flex-col">
              <Quote size={32} className="text-[#4F9622] mb-4"/>
              <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">CUSTOMER SUCCESS STORY</div>
              <p className="text-lg lg:text-xl text-[#15348C] leading-relaxed">
                Skillsgain delivered a customized Software Testing and Quality Assurance learning program covering automation, AI testing, and enterprise QA practices. Our engineering teams improved testing efficiency, software quality, and release confidence through practical labs and real-world projects.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        <Reveal variant="scale">
          <div className="bg-[#15348C] rounded-3xl p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#8BD835]/20 rounded-full blur-3xl"/>
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR QA & ENGINEERING LEADERS</div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Deliver Reliable Software with Enterprise Testing Training</h2>
                <p className="mt-3 text-gray-300">Prepare QA professionals and software engineering teams with enterprise learning in Software Testing, Software Quality Assurance (QA), AI Testing, automation frameworks, performance testing, and security testing.</p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Link to="/contact" className="sg-btn-glow bg-[#8BD835] hover:bg-[#6BBE32] text-white font-semibold px-6 py-3 rounded-md transition-colors">Talk to Our Experts</Link>
                <Link to="/contact" className="border-2 border-white/40 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request Corporate Proposal</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer/>
    </div>
  );
};

export default TestingPage;
